/**
 * CounterpointEngine.js
 * Implements "Horizontal" enhancements for Intelligent Call & Response.
 * Analyzes melody rhythm density to fill gaps.
 */

import { checkClash } from '../RegistryUtils.js';

const COUNTERPOINT_CONFIG = {
    scoring: {
        preferredDirectionBoost: 0.5,
        otherDirectionPenalty: 1.5,
        clashPenalty: 50
    },
    gapFill: {
        restChanceWhenActive: 0.8,
        playChanceWhenInactive: 0.9,
        minPassingDuration: 1.0
    }
};

/**
 * Generates a complementary counter-line using analysis data.
 * @param {Object} chord - Current chord context.
 * @param {number} barStart - Start time of bar.
 * @param {Object} analysis - The melody analysis (activityMap, contourMap).
 * @param {Object} context - { prevNote, instrument, scale, nextChord, voiceRegistry }
 */
export const generateComplementaryLine = (chord, barStart, analysis, context = {}) => {
    if (!analysis) return []; // Fallback handled by caller? Or return empty.

    const events = [];
    const beatOffset = Math.floor(barStart); // Absolute beat index
    const activityMap = analysis.activityMap || [];

    // ENHANCEMENT: Instrument-Aware Ranges
    const instrRange = (context.instrument && context.instrument.range) ? context.instrument.range : { min: 48, max: 72 };
    const MIN_PITCH = instrRange.min || 48;
    const MAX_PITCH = instrRange.max || 72;

    let currentPitch = context.prevNote || (MIN_PITCH + 7 + chord.rootPitchClass % 12);
    // Snap start pitch
    if (currentPitch < MIN_PITCH) currentPitch = MIN_PITCH + 7;
    if (currentPitch > MAX_PITCH) currentPitch = MAX_PITCH - 7;

    const notes = chord.notes || [chord.rootPitchClass + 60];

    // Iterate 4 beats of the bar
    for (let beat = 0; beat < 4; beat++) {
        const absBeat = beatOffset + beat;
        const activity = activityMap[absBeat] || 0; // 0.0 to 1.0 (Density)
        const time = barStart + beat;

        // --- STRATEGY SELECTION BASED ON ACTIVITY ---

        // Case 1: Melody is Busy (Activity > 0.5)
        // Counterpoint: Sustain (Whole/Half) or Rest
        if (activity > 0.6) {
            // Generate Long Note (2 beats) if starting on strong beat
            if (beat % 2 === 0) {
                // Find stable chord tone, AVOID current pitch to force movement
                const target = findClosestChordTone(currentPitch, notes, 0, 'closest', currentPitch, context.voiceRegistry, time);
                events.push({
                    s: time,
                    d: target,
                    len: 2.0,
                    vel: 0.6,
                    role: 'counter_melody',
                    articulation: 'long'
                });
                beat++; // Skip next beat
                currentPitch = target;
                continue;
            }
        }
        // Case 2: Melody is Sparse (Activity < 0.3)
        // Counterpoint: Moving line (8ths/16ths)
        else {
            // 2 x 8th notes
            // Avoid current pitch for target1
            const target1 = findClosestChordTone(currentPitch, notes, 0.5, 'closest', currentPitch, context.voiceRegistry, time);

            // Passing note logic...

            events.push({
                s: time,
                d: target1, // Jump to new note immediately
                len: 0.5,
                vel: 0.7,
                articulation: 'legato'
            });

            // Move pitch for next 8th
            // Avoid target1
            const nextPitch = findClosestChordTone(target1, notes, 0.8, 'closest', target1, context.voiceRegistry, time + 0.5);

            events.push({
                s: time + 0.5,
                d: nextPitch,
                len: 0.5,
                vel: 0.6,
                articulation: 'legato'
            });

            currentPitch = nextPitch;
        }
    }

    return events;
};

/**
 * Checks for voice crossing against the melody analysis.
 * @param {number} pitch - Candidate pitch.
 * @param {number} time - Absolute time.
 * @param {Object} analysis - Melody analysis.
 * @returns {number} - Adjusted pitch (safe).
 */
export const checkVoiceCrossing = (pitch, time, analysis) => {
    if (!analysis || !analysis.registerMap) return pitch;

    const barIndex = Math.floor(time / 4);
    const reg = analysis.registerMap[barIndex];

    if (reg && reg.min !== Infinity) {
        // Ensure we are below the melody range
        // Threshold: 2 semitones buffer
        if (pitch > (reg.min - 2)) {
            // Too high! Drop octave.
            return pitch - 12;
        }
    }
    return pitch;
};

/**
 * Generates a counter-line that complements the main melody.
 * (Legacy Wrapper for compatibility, redirects to generateComplementaryLine if analysis provided)
 * @param {Array} melodyEvents - The main melody events (Lead).
 * @param {Array} harmonyChords - The underlying chord progression.
 * @param {Object} scale - The key/scale context (scale intervals array).
 * @param {Object} context - { barStart, prevNote, instrument, analysis, voiceRegistry }
 */
export const generateCounterLine = (melodyEvents, harmonyChords, scale, context = {}) => {
    if (context.analysis) {
        // Use New Intelligent Engine
        // Find current chord
        const chord = harmonyChords.find(c => context.barStart >= (c.barIndex * 4) && context.barStart < (c.barIndex * 4 + c.duration));
        if (!chord) return [];

        let events = generateComplementaryLine(chord, context.barStart, context.analysis, context);

        // Post-process: Check Crossings
        events = events.map(ev => ({
            ...ev,
            d: checkVoiceCrossing(ev.d, ev.s, context.analysis)
        }));

        return events;
    }

    // --- FALLBACK (Old Logic) ---
    if (!melodyEvents || melodyEvents.length === 0) return [];
    if (!harmonyChords || !Array.isArray(harmonyChords)) return [];

    const counterEvents = [];
    const barStart = context.barStart || 0;
    const barEnd = barStart + 4; // Assume 4/4 for this chunk

    // 1. Analyze Melody Density in this bar (Activity Map)
    // We split the bar into 8th note slots (8 slots)
    // 0 = Empty, 1 = Occupied
    const activityMap = new Array(8).fill(0);
    // Also track avg pitch movement
    let melodySlope = 0;
    let noteCount = 0;
    let lastMelodyNote = null;

    melodyEvents.forEach(ev => {
        if (ev.s >= barStart && ev.s < barEnd) {
            const relStart = ev.s - barStart;
            const startSlot = Math.floor(relStart * 2); // 8th note grid
            const durSlots = Math.ceil(ev.len * 2);

            for(let i=0; i<durSlots; i++) {
                if (startSlot + i < 8) activityMap[startSlot + i] = 1;
            }

            if (ev.d) {
                if (lastMelodyNote !== null) {
                    melodySlope += (ev.d - lastMelodyNote);
                }
                lastMelodyNote = ev.d;
                noteCount++;
            }
        }
    });

    const averageSlope = noteCount > 1 ? melodySlope / (noteCount - 1) : 0;
    const contraryDir = averageSlope > 0 ? 'down' : (averageSlope < 0 ? 'up' : 'closest');

    // 2. Identify Gaps (where activity is 0)
    // Rule: If Melody is active, Counterpoint should be Sustained or Rest.
    // Rule: If Melody is resting, Counterpoint should move.

    // Find current chord
    const chord = harmonyChords.find(c => barStart >= (c.barIndex * 4));
    if (!chord) return [];

    // ENHANCEMENT: Instrument-Aware Ranges
    const instrRange = (context.instrument && context.instrument.range) ? context.instrument.range : { min: 48, max: 72 };
    // Safe fallback range (Tenor)
    const MIN_PITCH = instrRange.min || 48;
    const MAX_PITCH = instrRange.max || 72;

    let currentPitch = context.prevNote || (MIN_PITCH + 7 + chord.rootPitchClass % 12);

    // Adjust start pitch to range
    if (currentPitch < MIN_PITCH) currentPitch = MIN_PITCH + (chord.rootPitchClass % 12);
    if (currentPitch > MAX_PITCH) currentPitch = MAX_PITCH - 12 + (chord.rootPitchClass % 12);

    for (let slot = 0; slot < 8; slot++) {
        const time = barStart + (slot * 0.5);
        const isMelodyActive = activityMap[slot];

        // DECISION LOGIC
        let shouldPlay = false;

        if (!isMelodyActive) {
            shouldPlay = Math.random() < COUNTERPOINT_CONFIG.gapFill.playChanceWhenInactive;
        } else {
            // Counterpoint during melody? Only if harmony/sustained.
            if (slot % 2 === 0 && Math.random() > COUNTERPOINT_CONFIG.gapFill.restChanceWhenActive) shouldPlay = true;
        }

        if (shouldPlay) {
            // Duration? Look ahead for next melody activity
            let duration = 0.5;
            for(let next=slot+1; next<8; next++) {
                if (activityMap[next] === 0) duration += 0.5;
                else break; // Stop when melody enters
            }

            // Generate Note
            // Move stepwise from currentPitch to a chord tone
            let target = findClosestChordTone(currentPitch, chord.notes, 1, contraryDir, null, context.voiceRegistry, time);

            // Constraint Check
            if (target < MIN_PITCH) target += 12;
            if (target > MAX_PITCH) target -= 12;

            // ENHANCEMENT: PASSING NOTES
            if (Math.abs(target - currentPitch) > 2 && Math.abs(target - currentPitch) < 6 && duration >= COUNTERPOINT_CONFIG.gapFill.minPassingDuration) {
                 // Try to split note
                 const passingNote = Math.round((currentPitch + target) / 2); // Rough approximation
                 // Better: snap to scale? (omitted for brevity, chromatic passing is okay or simple avg)

                 counterEvents.push({
                     s: time,
                     d: passingNote, // The passing tone
                     len: 0.5,
                     vel: 0.5,
                     role: 'counter_melody',
                     articulation: 'legato',
                     type: 'passing'
                 });

                 counterEvents.push({
                     s: time + 0.5,
                     d: target, // The target chord tone
                     len: duration - 0.5,
                     vel: 0.6 + (Math.random() * 0.2),
                     role: 'counter_melody',
                     articulation: 'legato'
                 });
            } else {
                counterEvents.push({
                    s: time,
                    d: target,
                    len: duration,
                    vel: 0.6 + (Math.random() * 0.2),
                    role: 'counter_melody',
                    articulation: duration < 0.5 ? 'spiccato' : 'legato'
                });
            }

            currentPitch = target;

            // Advance loop by duration (convert to slots)
            const slotsUsed = Math.floor(duration * 2);
            slot += (slotsUsed - 1);
        }
    }

    return counterEvents;
};

const findClosestChordTone = (pitch, chordNotes, randomization = 0, preferredDirection = 'closest', avoidPitch = null, registry = null, time = 0) => {
    // chordNotes are usually [60, 64, 67] etc.
    // Normalize to pitch class
    const pcs = (Array.isArray(chordNotes) ? chordNotes : []).map(n => n % 12);

    // Find neighbors in current octave
    const octave = Math.floor(pitch / 12);
    let candidates = [];

    [-1, 0, 1].forEach(octOffset => {
        pcs.forEach(pc => {
            candidates.push(pc + ((octave + octOffset) * 12));
        });
    });

    // Filter out avoidPitch (Prevent repetition)
    if (avoidPitch !== null) {
        const filtered = candidates.filter(c => c !== avoidPitch);
        if (filtered.length > 0) candidates = filtered;
    }

    // Single Sort Logic (Optimized)
    candidates.sort((a,b) => {
        const distA = Math.abs(a - pitch);
        const distB = Math.abs(b - pitch);

        // Registry Check (Penalize Clashes)
        let clashA = 0;
        let clashB = 0;
        if (registry) {
            clashA = checkClash(registry, time, a);
            clashB = checkClash(registry, time, b);
        }

        // Add clash penalty to distance score (effectively making it "farther")
        const scoreDistA = distA + clashA;
        const scoreDistB = distB + clashB;

        if (preferredDirection === 'closest') {
            return scoreDistA - scoreDistB;
        }

        const dirA = (a - pitch) > 0 ? 'up' : 'down';
        const dirB = (b - pitch) > 0 ? 'up' : 'down';

        // Apply penalty/boost to distance based on direction
        const scoreA = scoreDistA * (dirA === preferredDirection ? COUNTERPOINT_CONFIG.scoring.preferredDirectionBoost : COUNTERPOINT_CONFIG.scoring.otherDirectionPenalty);
        const scoreB = scoreDistB * (dirB === preferredDirection ? COUNTERPOINT_CONFIG.scoring.preferredDirectionBoost : COUNTERPOINT_CONFIG.scoring.otherDirectionPenalty);

        return scoreA - scoreB;
    });

    // Pick closest or 2nd closest based on randomization factor
    let choice = candidates[0];
    if (Math.random() < randomization && candidates.length > 1) {
        choice = candidates[1];
    }

    return choice;
};
